/**
 * DashcamFAQPage — customer-facing explainer page for the in-vehicle
 * Cautio dashcam system (front + cabin + rear).
 *
 * Currently a deliberate placeholder: layout, header, footer, and CTA are
 * production-ready — only the body content is empty. The actual Q&A
 * content will be authored by the operator and slotted into `faqData`
 * below in a follow-up PR; the structure mirrors `FAQPage.jsx` so the
 * existing collapsible card pattern just works once items land.
 *
 * Route: /dashcam-faqs (mounted in App.js).
 * Discovery: linked from the SafetyTechSection on the home page, next to
 * the "Learn more about Cautio" external link.
 */
import { useState } from "react";
import { ArrowLeft, Video, ChevronDown, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Authored by the Sarathi-X / Cautio teams. Keep `q`/`a` shape so the
// FaqItem renderer below works without changes. `\n\n` creates a blank
// line between paragraphs (rendered via the `whitespace-pre-line` class
// on the answer <p>); bullet lists use a leading "• ".
const faqData = [
  {
    category: "Privacy & Access",
    items: [
      {
        q: "Who, apart from Sarathi-X founders and Cautio's personnel, can see the dashcam footage?",
        a: "Dashcam footage access is strictly controlled and limited only to authorised users.\n\nAccess is managed through the Cautio Command Center platform, where approved Sarathi-X and Cautio users can access footage based on their roles and responsibilities. Platform access is provided only through registered user accounts approved by the administrator.",
      },
      {
        q: "Can the driver access the dashcam footage?",
        a: "Drivers do not have access to dashcam footage unless specific access has been authorised.",
      },
      {
        q: "Is the in-cabin camera recording me at all times during the ride?",
        a: "Recording happens when the vehicle is switched ON. The recording is used for passenger safety, driver safety, and incident investigation purposes.",
      },
      {
        q: "Is there audio recording inside the cabin?",
        a: "The Cautio dashcam supports audio recording capability through an in-built microphone.\n\nAudio recording availability depends on the camera's configuration and settings enabled as per operational requirements.",
      },
    ],
  },
  {
    category: "Data Storage & Retention",
    items: [
      {
        q: "Where is the footage stored — on the device or on a server?",
        a: "Footage is stored using both local device storage and cloud storage.\n\nContinuous trip footage is stored locally on the device SD card for a limited duration.\n\nSafety events, alerts, or required footage clips may be securely uploaded to the Cautio platform for review and incident management.",
      },
      {
        q: "In which country is the footage stored?",
        a: "Cautio platform data and servers are hosted in India.",
      },
      {
        q: "How long is the footage retained?",
        a: "Continuous footage stored on the device is retained for a limited duration based on device configuration, storage capacity, and vehicle usage.\n\nThe dashcam works on a rolling storage mechanism, where older footage is automatically overwritten as new footage is recorded once storage capacity is reached.\n\nImportant safety events or footage required for incident review may be securely retained on the Cautio platform as per operational requirements.",
      },
    ],
  },
  {
    category: "Your Rights & Requests",
    items: [
      {
        q: "Can I request my footage after a ride?",
        a: "Yes. You can request footage related to your ride by contacting Sarathi-X support.\n\nRequests should be raised as soon as possible because device footage is available only for a limited duration before being automatically overwritten.",
      },
      {
        q: "Can I request my footage to be deleted after a ride?",
        a: "Regular footage stored on the device follows an automatic overwrite process and is removed after the retention period.\n\nFor any specific data-related requests, passengers can contact Sarathi-X support.",
      },
      {
        q: "Is the footage ever shared with third parties?",
        a: "No. Footage is not shared with unauthorised third parties.\n\nAccess is restricted and provided only for legitimate safety, operational, investigation, or compliance-related requirements.",
      },
    ],
  },
  {
    category: "Security & Technology",
    items: [
      {
        q: "Is the footage encrypted?",
        a: "Cautio follows security practices designed to protect customer data and restrict unauthorised access.\n\nFootage access is controlled through secure platform authentication, role-based permissions, and authorised workflows.",
      },
      {
        q: "How quickly can footage be retrieved when I report an incident?",
        a: "If the device is online, connected to the network, and the footage is still available on the device, footage can usually be retrieved within minutes.\n\nRetrieval time may vary depending on:\n• Network connectivity\n• Size of the requested footage\n• Duration of the incident\n• Time required to identify the relevant clip",
      },
      {
        q: "Has the dashcam system been used in any real incident resolutions?",
        a: "Yes. Cautio dashcam footage has helped in multiple real-world situations by providing reliable video evidence for incident investigation, insurance-related cases, and vehicle safety matters.",
      },
      {
        q: "Is recording continuous or event-triggered?",
        a: "Both.\n\nThe dashcam continuously records footage locally on the device storage.\n\nAdditionally, AI-based safety events generate alerts where relevant event clips are uploaded to the platform for faster review and action.",
      },
      {
        q: "What security measures are implemented to protect customer footage?",
        a: "Cautio protects customer footage through:\n• Controlled platform access\n• Authorised user permissions\n• Secure login-based authentication\n• Restricted footage retrieval\n• Operational review processes\n\nOnly approved users with valid access can retrieve or review footage.",
      },
      {
        q: "Is Cautio compliant with India's Digital Personal Data Protection Act (DPDP)?",
        a: "Cautio follows responsible data handling practices and continuously works towards alignment with applicable Indian data protection and privacy requirements.\n\nFor specific legal or compliance-related queries, please contact the Cautio team.",
      },
    ],
  },
  {
    category: "About the System",
    items: [
      {
        q: "What are the most important things a passenger should know about how their footage is handled?",
        a: "• Cautio's dashcam technology is installed to improve passenger and driver safety.\n• The system helps create a safer environment inside vehicles through video-based safety monitoring.\n• Access to footage is controlled and restricted to authorised users only.\n• Driver Monitoring System (DMS) features help encourage responsible driving behaviour.\n• Event-based alerts allow operators to identify and respond to safety situations quickly.\n• Footage is used for safety, accountability, and incident resolution.\n\nThe purpose of recording is to make every journey safer for passengers and drivers.\n\nFor questions or to submit a data request, please contact Sarathi-X support or reach out to Cautio's team.",
      },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
        data-testid="dashcam-faq-item-toggle"
      >
        <span className="text-white font-medium text-sm">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-sarathi-text-muted transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="text-sarathi-text-secondary text-sm leading-relaxed pb-4 whitespace-pre-line">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function DashcamFAQPage() {
  return (
    <div className="min-h-screen bg-sarathi-background text-white" data-testid="dashcam-faq-page">
      {/* Sticky breadcrumb header — mirrors FAQPage for visual continuity */}
      <div className="border-b border-white/10 bg-sarathi-surface/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/"
            className="w-9 h-9 rounded-full bg-sarathi-background border border-white/10 flex items-center justify-center text-sarathi-text-muted hover:text-white transition-colors"
            data-testid="dashcam-faq-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-sarathi-primary" />
            <h1 className="font-outfit text-lg font-semibold">Dashcam FAQs</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8" data-testid="dashcam-faq-content">
        <div>
          <h2 className="font-outfit text-2xl font-bold">About the Sarathi-X Dashcam</h2>
          <p className="text-sarathi-text-muted text-sm mt-2 leading-relaxed">
            Every Sarathi-X vehicle is fitted with a Cautio dual-channel AI
            dashcam — front road view, cabin view, and rear road view —
            with alerts sent to the Sarathi-X Team and the Cautio command
            centre. This page answers the questions our riders most often
            ask about how it works, what it records, and how we protect
            your privacy.
          </p>
        </div>

        {/* Privacy highlight strip — visible even while the Q&A is empty so
            riders get the most important assurance up front. */}
        <div className="bg-sarathi-primary/5 border border-sarathi-primary/20 rounded-xl p-5 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-sarathi-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-semibold mb-1">Recording is consent-based</p>
            <p className="text-sarathi-text-secondary text-sm leading-relaxed">
              Footage is captured for safety and dispute resolution only.
              Access is restricted, retention is time-bound, and clips are
              never shared without a lawful request or your explicit consent.
              Full details will appear here shortly.
            </p>
          </div>
        </div>

        {/* Q&A list — empty by default; renders automatically once items
            are added to faqData above. */}
        {faqData.length === 0 ? (
          <div className="bg-sarathi-surface border border-white/10 rounded-xl px-6 py-8 text-center" data-testid="dashcam-faq-empty">
            <p className="text-sarathi-text-secondary text-sm">
              Detailed Q&amp;A is on the way. In the meantime, if you have a
              specific question about how the dashcam works in your ride,
              please reach out on WhatsApp below — we&apos;re happy to walk
              you through it.
            </p>
          </div>
        ) : (
          faqData.map((section) => (
            <div key={section.category}>
              <h3 className="font-outfit text-base font-semibold uppercase tracking-wide mb-3 text-sarathi-primary">
                {section.category}
              </h3>
              <div className="bg-sarathi-surface border border-white/10 rounded-xl px-5">
                {section.items.map((item) => (
                  <FaqItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Footer CTA — copies the FAQ page pattern */}
        <div className="border-t border-white/10 pt-6 text-center">
          <p className="text-sarathi-text-muted text-sm mb-3">Have a specific dashcam question?</p>
          <a
            href="https://wa.me/919187057303?text=Hi%2C%20I%20have%20a%20question%20about%20the%20Sarathi-X%20dashcam."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-5 py-2.5 text-[#25D366] text-sm font-medium hover:bg-[#25D366]/20 transition-colors"
            data-testid="dashcam-faq-whatsapp-cta"
          >
            Chat with us on WhatsApp
          </a>
          <div className="mt-4">
            <Link to="/" className="text-sarathi-primary hover:underline text-sm font-medium" data-testid="dashcam-faq-home-link">
              Back to Sarathi-X Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
